import { useState, forwardRef, useEffect, useContext, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    Slide,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Chip,
    Button,
    FormControl,
    TextField
} from '@mui/material';
import { ArrowBackIosNew, ArrowForwardIos, Close } from '@mui/icons-material';
// Our Components
import { AuthContext } from 'context/auth-context';
import ChildTransactionsTable from '../TransactionsSection/table/ChildTransactionsTable.js';
import TransactionsSummaryFooter from '../TransactionsSection/table/TransactionsSummaryFooter.js';
// Services
import { Cards } from 'lib/api';
import { Businesses } from 'lib/api';
// Utils and Helpers
import { CARD_TRANSACTION_TYPE, CARD_TYPE } from 'helpers/constants';
import { numberFormat } from 'helpers/i18n';
// Assets
import { ReactComponent as AttachmentsIcon } from 'assets/AttachmentsIcon.svg';
import { ReactComponent as CalendarIcon } from 'assets/CalendarIcon.svg';
import { ReactComponent as CardCategoryIcon } from 'assets/CardCategoryIcon.svg';
import { ReactComponent as CardIcon } from 'assets/CardIcon.svg';
import { ReactComponent as IdentificationIcon } from 'assets/IdentificationIcon.svg';
import { ReactComponent as MailIcon } from 'assets/MailIcon.svg';
import { ReactComponent as MemoIcon } from 'assets/MemoIcon.svg';
import { ReactComponent as ReceiptFileIcon } from 'assets/transactionsSection/receipt.svg';
import { ReactComponent as StoreIcon } from 'assets/StoreIcon.svg';
import { ReactComponent as TransactionCategoryIcon } from 'assets/transactionsSection/TransactionCategoryIcon.svg';
import { ReactComponent as TransactionIdIcon } from 'assets/transactionsSection/transactionIdIcon.svg';
import { ReactComponent as UploadIcon } from 'assets/transactionsSection/upload-receipt-icon.svg';
import './AchCardTransactionDetails.css';

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} />;
});
const AchCardTransactionDetails = ({ isOpen, setIsOpen, cardTransactionID, achCardTransactionsRef }) => {
    const history = useHistory();
    const { businessInfo } = useContext(AuthContext);
    const [cardTransaction, setCardTransaction] = useState({
        memo: '',
        receipts: [],
        id_business_transaction_category: null
    });
    const [parentCardTransaction, setParentCardTransaction] = useState({
        memo: '',
        receipts: [],
        id_business_transaction_category: null
    });
    const [transactionType, setTransactionType] = useState({
        description: 'amount paid'
    });
    const [isChildTransaction, setIsChildTransaction] = useState(false);
    const [transactionId, setTransactionId] = useState(cardTransactionID);
    const [verifier, setVerifier] = useState(false);
    const [childrenTransactions, setChildrenTransactions] = useState([]);
    const [childTransactionsTotalAmount, setChildTransactionsTotalAmount] = useState(0);
    const [cardTransactionCategory, setCardTransactionCategory] = useState({});
    const [prevCardTransaction, setPrevCardTransaction] = useState(null);
    const [nextCardTransaction, setNextCardTransaction] = useState(null);

    const updateTransactionType = type => {
        let newDescription;

        switch (type) {
            case CARD_TRANSACTION_TYPE.SPEND:
                newDescription = 'amount paid';
                break;
            case CARD_TRANSACTION_TYPE.REFUND:
                newDescription = 'amount refunded';
                break;
            case CARD_TRANSACTION_TYPE.REVERSAL:
                newDescription = 'amount reversed';
                break;
            case CARD_TRANSACTION_TYPE.DECLINED:
                newDescription = 'amount declined';
                break;
            case CARD_TRANSACTION_TYPE.REPLENISH:
                newDescription = 'amount replenished';
                break;
            default:
                return;
        }

        setTransactionType({
            description: newDescription
        });
    };
    const fetchCardTransaction = useCallback(async () => {
        try {
            const cardTransactionData = await Cards.getCardTransaction(transactionId);
            setVerifier(cardTransactionData?.id);

            let childrenTransactionsData = await Cards.getCardTransactionChildren(transactionId);

            if (cardTransactionData?.id_parent_card_transaction) {
                setIsChildTransaction(true);
                const parentCardTransactionData = await Cards.getCardTransaction(cardTransactionData?.id_parent_card_transaction);
                setParentCardTransaction(parentCardTransactionData);

                childrenTransactionsData = await Cards.getCardTransactionChildren(cardTransactionData.id_parent_card_transaction);
                setChildTransactionsTotalAmount(
                    parseFloat(childrenTransactionsData.totalAmount) + parseFloat(parentCardTransactionData.amount)
                );
            }

            setCardTransaction(cardTransactionData);
            setChildrenTransactions({
                ...childrenTransactionsData,
                totalAmount: parseFloat(childrenTransactionsData.totalAmount) + parseFloat(cardTransactionData.amount)
            });
        } catch (error) {
            console.error(error);
        }
    }, [transactionId]);
    useEffect(() => {
        setIsChildTransaction(false);
        fetchCardTransaction();
    }, [fetchCardTransaction]);

    useEffect(() => {
        updateTransactionType(cardTransaction.type);
    }, [cardTransaction.type]);

    const fetchTransactionCategories = useCallback(async () => {
        try {
            const transactionsCategoryList = await Businesses.getTransactionsCategories(businessInfo.id);
            if (isChildTransaction) {
                setCardTransactionCategory(
                    transactionsCategoryList?.find(item => item.id === parentCardTransaction?.id_business_transaction_category)
                );
            } else {
                setCardTransactionCategory(
                    transactionsCategoryList?.find(item => item.id === cardTransaction?.id_business_transaction_category)
                );
            }
        } catch (error) {
            console.error(error);
        }
    }, [
        businessInfo?.id,
        cardTransaction?.id_business_transaction_category,
        isChildTransaction,
        parentCardTransaction?.id_business_transaction_category
    ]);
    useEffect(() => {
        fetchTransactionCategories();
    }, [fetchTransactionCategories]);

    useEffect(() => {
        if (cardTransaction?.id && achCardTransactionsRef.current?.results?.length > 0) {
            const achCardTransactions = achCardTransactionsRef.current;
            const index = achCardTransactions.results.findIndex(item => item.id === cardTransaction.id);

            const prevCardTransaction = index > 0 ? achCardTransactions.results[index - 1] : null;
            const nextCardTransaction =
                index < achCardTransactions.results.length - 1 ? achCardTransactions.results[index + 1] : null;

            setPrevCardTransaction(prevCardTransaction);
            setNextCardTransaction(nextCardTransaction);
        }
    }, [cardTransaction.id, achCardTransactionsRef]);

    const handleTransactionDetailsClose = async () => {
        setIsOpen(false);
    };

    const handleRedirect = targetTransactionID => {
        setTransactionId(targetTransactionID);
    };

    const statusFormatter = cardTransaction => {
        let transactionTypeName;
        if (cardTransaction?.type === CARD_TRANSACTION_TYPE.SPEND_UPDATE) {
            transactionTypeName = cardTransaction?.type?.split('.')[2];
        } else {
            transactionTypeName = cardTransaction?.type?.split('.')[1];
        }
        const parentTransaction = cardTransaction?.id_parent_card_transaction;

        let label = transactionTypeName;
        if (parentTransaction) {
            label += ` (${'...' + parentTransaction.substring(parentTransaction.length - 6)})`;
        }

        if (cardTransaction?.type === CARD_TRANSACTION_TYPE.DECLINED) {
            return <Chip label={label} className="chip chip-danger fw-bold" />;
        }
        if (cardTransaction?.type === CARD_TRANSACTION_TYPE.REFUND) {
            return <Chip label={label} className="chip chip-warning fw-bold" />;
        }
        if (cardTransaction?.type === CARD_TRANSACTION_TYPE.SPEND) {
            return <Chip label={label} className="chip chip-success fw-bold" />;
        }
        if (cardTransaction?.type === CARD_TRANSACTION_TYPE.SPEND_UPDATE) {
            return <Chip label={label} className="chip chip-success fw-bold" />;
        }
        if (cardTransaction?.type === CARD_TRANSACTION_TYPE.REVERSAL) {
            return <Chip label={label} className="chip chip-danger fw-bold" />;
        }
        if (cardTransaction?.type === CARD_TRANSACTION_TYPE.REPLENISH) {
            return <Chip label={label} className="chip chip-pending fw-bold" />;
        }
    };

    const handleRedirectClick = cardTransactionID => {
        history.push({
            pathname: '/transactions',
            search: `?cardTransactionId=${cardTransactionID}`
        });
    };

    return (
        <Dialog
            id="ach-card-transaction-details"
            fullScreen
            open={isOpen}
            onClose={handleTransactionDetailsClose}
            TransitionComponent={Transition}>
            <DialogContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="15px">
                    <Box className="edit-card-funds__modal-title">Transaction Details</Box>

                    <Box alignSelf="start">
                        <IconButton
                            disabled={!prevCardTransaction}
                            onClick={() => handleRedirect(prevCardTransaction?.id)}
                            sx={{
                                marginRight: '15px',
                                border: !prevCardTransaction ? '1px solid gray' : '1px solid #1BABE2',
                                borderRadius: '8px'
                            }}>
                            <ArrowBackIosNew
                                sx={{ fontSize: '15px', cursor: 'pointer', color: !prevCardTransaction ? 'gray' : '#1BABE2' }}
                            />
                        </IconButton>
                        <IconButton
                            disabled={!nextCardTransaction}
                            onClick={() => handleRedirect(nextCardTransaction?.id)}
                            sx={{
                                marginRight: '10px',
                                border: !nextCardTransaction ? '1px solid gray' : '1px solid #1BABE2',
                                borderRadius: '8px'
                            }}>
                            <ArrowForwardIos
                                sx={{ fontSize: '15px', cursor: 'pointer', color: !nextCardTransaction ? 'gray' : '#1BABE2' }}
                            />
                        </IconButton>
                        <IconButton onClick={handleTransactionDetailsClose}>
                            <Close sx={{ fontSize: '20px', cursor: 'pointer' }} />
                        </IconButton>
                    </Box>
                </Box>
                <Box className="ach-card-transaction-details__banner">
                    <Box marginBottom="5px">{statusFormatter(cardTransaction)}</Box>

                    <Box component="h3" className="ach-card-transaction-details__transaction-title">
                        {numberFormat(cardTransaction.amount, 2, 2)}
                    </Box>

                    <Box component="p" className="ach-card-transaction-details__transaction-description">
                        {transactionType.description}
                    </Box>
                </Box>
                {!isChildTransaction &&
                    childrenTransactions?.results?.length > 0 &&
                    cardTransaction.balance &&
                    cardTransaction?.id === verifier && (
                        <Box display="flex" flexDirection="column" alignItems="flex-start" marginTop="auto" width="100%">
                            <Box width="100%" marginLeft="auto" marginRight="auto">
                                <ChildTransactionsTable
                                    childrenTransactions={childrenTransactions}
                                    setTransactionId={setTransactionId}
                                />
                            </Box>
                        </Box>
                    )}
                {isChildTransaction && (
                    <Box display="flex" flexDirection="column" alignItems="flex-start" marginTop="auto" width="100%">
                        <Box width="100%" marginLeft="auto" marginRight="auto">
                            <TransactionsSummaryFooter childTransactionsTotalAmount={childTransactionsTotalAmount} />
                        </Box>
                    </Box>
                )}
                <Box display="flex" flex justifyContent="start" alignItems="flex-start" marginBottom="26px" marginTop="5px">
                    <Table className="ach-card-transaction-details__table" size="small">
                        <TableBody>
                            <TableRow className="transition-details__row" marginBottom="25px">
                                <TableCell sx={{ borderBottom: 'none' }}>
                                    <Box className="ach-card-transaction-details__data-label">
                                        <CalendarIcon />
                                        <Box marginLeft="11.5px"> Created Date</Box>
                                    </Box>
                                </TableCell>
                                <TableCell className="ach-card-transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                    {dayjs(cardTransaction?.solid_created_at).format('MM/DD/YYYY')}
                                </TableCell>
                            </TableRow>
                            <TableRow className="transition-details__row" marginBottom="25px">
                                <TableCell sx={{ borderBottom: 'none' }}>
                                    <Box className="ach-card-transaction-details__data-label">
                                        <TransactionIdIcon />
                                        <Box marginLeft="11.5px"> Transaction Id</Box>
                                    </Box>
                                </TableCell>
                                <TableCell className="ach-card-transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                    {cardTransaction?.id}
                                </TableCell>
                            </TableRow>
                            {isChildTransaction && (
                                <TableRow marginBottom="25px">
                                    <TableCell sx={{ borderBottom: 'none' }}>
                                        <Box className="ach-card-transaction-details__data-label">
                                            <TransactionIdIcon />
                                            <Box marginLeft="11.5px"> Parent Transaction Id</Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        className="ach-card-transaction-details__data-value"
                                        sx={{
                                            borderBottom: 'none',
                                            color: '#4ABDE9'
                                        }}>
                                        <a
                                            onClick={() => handleRedirect(parentCardTransaction?.id)}
                                            style={{ cursor: 'pointer' }}>
                                            {parentCardTransaction?.id}
                                        </a>
                                    </TableCell>
                                </TableRow>
                            )}
                            <TableRow marginBottom="25px">
                                <TableCell sx={{ borderBottom: 'none' }}>
                                    <Box className="ach-card-transaction-details__data-label">
                                        <StoreIcon />
                                        <Box marginLeft="11.5px"> Paid to</Box>
                                    </Box>
                                </TableCell>
                                <TableCell className="ach-card-transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                    {cardTransaction?.data?.merchant?.merchantName ||
                                        cardTransaction?.data?.card?.merchant?.merchantName}
                                </TableCell>
                            </TableRow>
                            <TableRow className="transition-details__row" marginBottom="25px">
                                <TableCell sx={{ borderBottom: 'none' }}>
                                    <Box className="ach-card-transaction-details__data-label">
                                        <IdentificationIcon />
                                        <Box marginLeft="11.5px"> Paid by</Box>
                                    </Box>
                                </TableCell>
                                <TableCell className="ach-card-transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                    {cardTransaction?.card?.name ?? 'N/A'}
                                </TableCell>
                            </TableRow>
                            <TableRow className="transition-details__row" marginBottom="25px">
                                <TableCell sx={{ borderBottom: 'none' }}>
                                    <Box className="ach-card-transaction-details__data-label">
                                        <MailIcon />
                                        <Box marginLeft="11.5px"> Email Address</Box>
                                    </Box>
                                </TableCell>
                                <TableCell className="ach-card-transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                    {cardTransaction?.card?.email}
                                </TableCell>
                            </TableRow>
                            <TableRow className="transition-details__row" marginBottom="25px">
                                <TableCell sx={{ borderBottom: 'none' }}>
                                    <Box className="ach-card-transaction-details__data-label">
                                        <TransactionCategoryIcon />
                                        <Box marginLeft="11.5px"> Transaction Category</Box>
                                    </Box>
                                </TableCell>
                                <TableCell className="ach-card-transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                    <Box
                                        sx={{
                                            border: '1px solid #E0E0E0',
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: '4px',
                                            padding: '5px 10px',
                                            color: '#757575',
                                            cursor: 'not-allowed',
                                            '&:hover': {
                                                backgroundColor: '#f5f5f5'
                                            }
                                        }}>
                                        {cardTransactionCategory?.name ?? 'N/A'}
                                    </Box>
                                </TableCell>
                            </TableRow>
                            <TableRow className="transition-details__row" marginBottom="25px">
                                <TableCell sx={{ borderBottom: 'none' }}>
                                    <Box className="ach-card-transaction-details__data-label">
                                        <CardCategoryIcon />
                                        <Box marginLeft="11.5px"> Card Category</Box>
                                    </Box>
                                </TableCell>
                                <TableCell className="ach-card-transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                    {cardTransaction?.card?.label}
                                </TableCell>
                            </TableRow>
                            <TableRow className="transition-details__row" marginBottom="25px">
                                <TableCell sx={{ borderBottom: 'none' }}>
                                    <Box className="ach-card-transaction-details__data-label">
                                        <CardIcon />
                                        <Box marginLeft="11.5px"> Payment Method</Box>
                                    </Box>
                                </TableCell>
                                <TableCell className="ach-card-transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                    {cardTransaction?.card?.type === CARD_TYPE.PHYSICAL ? 'Physical' : 'Virtual'} Defi Card (*
                                    {cardTransaction?.card?.last4})
                                </TableCell>
                            </TableRow>
                            <TableRow className="transition-details__row" marginBottom="25px">
                                <TableCell
                                    sx={{
                                        borderBottom: 'none',
                                        color: '#617380',
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'flex-start'
                                    }}>
                                    <Box className="ach-card-transaction-details__data-label">
                                        <MemoIcon />
                                        <Box marginLeft="11.5px">Transaction Memo</Box>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ borderBottom: 'none', color: '#182C55' }}>
                                    <FormControl fullWidth>
                                        <TextField
                                            name="memo"
                                            multiline
                                            rows={3}
                                            size="small"
                                            placeholder="Add a transaction memo"
                                            value={cardTransaction?.memo ?? 'N/A'}
                                            disabled={true}
                                            sx={{ backgroundColor: '#f5f5f5' }}
                                        />
                                    </FormControl>
                                </TableCell>
                            </TableRow>
                            <TableRow className="transition-details__row" marginBottom="25px">
                                <TableCell
                                    sx={{
                                        borderBottom: 'none',
                                        color: '#617380',
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'flex-start'
                                    }}>
                                    <Box className="ach-card-transaction-details__data-label">
                                        <AttachmentsIcon />
                                        <Box marginLeft="11.5px"> Attachments</Box>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ borderBottom: 'none' }}>
                                    <Box
                                        component="label"
                                        className="ach-card-transaction-details__receipt-label"
                                        sx={{ border: '1px dashed #c0c7cc', cursor: 'default' }}>
                                        <Box>
                                            <Box
                                                component="span"
                                                className="ach-card-transaction-details__receipt-label-icon-container"
                                                marginRight="10px">
                                                <Box component="span">
                                                    <UploadIcon />
                                                </Box>
                                            </Box>
                                            <Box component="span" fontWeight="500" fontSize="13px">
                                                Upload{' '}
                                            </Box>
                                            <Box component="span" fontSize="10px" color="var(--secondary-color)">
                                                PDF, JPEG, PNG | Max 25 mb and up to 5 files
                                            </Box>
                                        </Box>
                                    </Box>
                                </TableCell>
                            </TableRow>
                            {cardTransaction.receipts.length > 0 && (
                                <TableRow className="transition-details__row" marginBottom="25px">
                                    <TableCell
                                        className="transition-details__placeholder"
                                        sx={{ borderBottom: 'none' }}></TableCell>
                                    <TableCell sx={{ borderBottom: 'none' }}>
                                        {cardTransaction.receipts.map(receipt => (
                                            <Box
                                                key={receipt?.id || receipt?.localId}
                                                component="label"
                                                className="ach-card-transaction-details__receipt-file"
                                                sx={{ marginBottom: '8px' }}>
                                                <Box display="flex" alignItems="center" padding="7px 0px 7px 16px">
                                                    <Box
                                                        component="span"
                                                        className="ach-card-transaction-details__receipt-file-icon-container"
                                                        marginRight="10px">
                                                        <ReceiptFileIcon />
                                                    </Box>
                                                    <Box
                                                        component="span"
                                                        className="ach-card-transaction-details__receipt-file-name">
                                                        {receipt?.name || receipt?.filename}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ))}
                                    </TableCell>
                                </TableRow>
                            )}
                            <TableRow className="transition-details__row">
                                <TableCell
                                    sx={{
                                        borderBottom: 'none'
                                    }}
                                />
                                <TableCell
                                    sx={{
                                        borderBottom: 'none',
                                        textAlign: 'right'
                                    }}>
                                    <Box marginTop="auto">
                                        <Button
                                            variant="contained"
                                            onClick={() => handleRedirectClick(cardTransaction?.id)}
                                            sx={{ marginTop: '40px' }}>
                                            Redirect to Edit
                                        </Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default AchCardTransactionDetails;
